import React, { ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { Canvas, FabricObject } from 'fabric';

import { EditorDataContext } from './editor-data-context';
import {
  type BindingType,
  type CanvasBinding,
  type CreativityEditorHandle,
  type LayerLabelResolver,
  type PieceModel
} from '@doer/frontend-components/creativity-editor';
import { resolveLayerLabel } from '../utils/resolve-layer-label';

import { CreativityType } from '../../../types/banner';
import type { RoutedFormData, FormDataValue } from '../../../types/templates';
import { useCreativityForm } from '../../../hooks/banners/creation/form/use-creativity-form';
import { getHandlerForTemplate } from '../../../handlers';
import { useCrossResolutionSync } from '../hooks/use-cross-resolution-sync';
import { useCanvasToFormSync } from '../hooks/use-canvas-to-form-sync';
import { useResolutionPreload } from '../hooks/use-resolution-preload';
import { useResolutionSnapshots } from '../hooks/use-resolution-snapshots';
import { useBindingPipeline } from '../hooks/use-binding-pipeline';
import { useActiveResolutionLoader } from '../hooks/use-active-resolution-loader';
import { useContentReady } from '../../../hooks/use-content-ready';

type EditorDataProviderProps = {
  children: ReactNode;
  creativityType: CreativityType;
  canvasBinding: CanvasBinding;
  defaultResolutionId?: string;
  contentId?: string;
  initialFormData?: RoutedFormData;
  savedModels?: Map<string, PieceModel>;
};

export const EditorDataProvider: React.FC<EditorDataProviderProps> = ({
  children,
  creativityType,
  canvasBinding,
  defaultResolutionId,
  contentId,
  initialFormData,
  savedModels
}) => {
  // Editor + canvas wiring.
  const editorRef = useRef<CreativityEditorHandle>(null);
  const [editorReady, setEditorReady] = useState(false);
  const [canvas, setCanvas] = useState<Canvas | null>(null);

  const onEditorReady = useCallback(() => {
    setCanvas(editorRef.current?.getCanvas() ?? null);
    setEditorReady(true);
  }, []);

  // Keeps a loader over the editor until the piece is rendered and its media has painted.
  const [mediaLoading, setMediaLoading] = useState(false);
  const [firstLoadDone, setFirstLoadDone] = useState(false);
  const onLoadingChange = useCallback((loading: boolean) => setMediaLoading(loading), []);
  const onFirstLoad = useCallback(() => setFirstLoadDone(true), []);
  const contentReady = useContentReady(editorReady && firstLoadDone, mediaLoading);

  // Active resolution selection.
  const resolutions = canvasBinding.resolutions;
  const initialResolutionId = defaultResolutionId ?? resolutions[0]?.id;
  const [activeResolutionId, setActiveResolutionId] = useState<string>(initialResolutionId);
  const activeResolution = useMemo(
    () => resolutions.find(r => r.id === activeResolutionId) ?? resolutions[0],
    [resolutions, activeResolutionId]
  );

  // Form state + defaults.
  const clientHandler = getHandlerForTemplate(creativityType);
  const computeDefaults = useCallback(
    (data: RoutedFormData, resolutionId?: string): RoutedFormData =>
      clientHandler.computeDefaults?.({ ...data, _creativityType: creativityType }, resolutionId) ?? {},
    [clientHandler, creativityType]
  );

  const {
    formData,
    errors,
    fields,
    isFormComplete,
    handleFieldChange: baseHandleFieldChange,
    setFieldError,
    validateForm
  } = useCreativityForm(creativityType, initialFormData);

  // Lets callbacks read the latest formData without stale closures.
  const formDataRef = useRef<RoutedFormData>(formData);
  formDataRef.current = formData;

  // Snapshot store + binding pipeline + resolution loader.
  const snapshots = useResolutionSnapshots({
    canvas,
    resolutions,
    activeResolutionId: activeResolution.id,
    fieldBindings: canvasBinding.fieldBindings,
    formDataRef,
    computeDefaults
  });

  const applyToModel = useCallback(
    (pieceId: string, bindingType: BindingType, value: unknown): boolean =>
      editorRef.current?.applyBindingToModel(pieceId, bindingType, value) ?? false,
    []
  );

  const getSavedModel = useCallback(
    (resolutionId: string): PieceModel | undefined => savedModels?.get(resolutionId),
    [savedModels]
  );

  const pipeline = useBindingPipeline({
    canvas,
    activeResolutionId: activeResolution.id,
    fieldBindings: canvasBinding.fieldBindings,
    computeDefaults,
    onAfterBinding: snapshots.patchOthers,
    applyToModel
  });

  useActiveResolutionLoader({
    editorRef,
    editorReady,
    canvas,
    activeResolution,
    fieldBindings: canvasBinding.fieldBindings,
    formDataRef,
    computeDefaults,
    getSnapshot: snapshots.get,
    saveSnapshot: snapshots.save,
    flushBindings: pipeline.flush,
    enqueueBinding: pipeline.enqueue,
    getSavedModel,
    onFirstLoad
  });

  // Form to canvas: enqueue a re-bind on every field change.
  const handleFieldChange = useCallback(
    async (fieldKey: string, value: FormDataValue): Promise<RoutedFormData> => {
      const next = await baseHandleFieldChange(fieldKey, value);
      if (canvas) pipeline.enqueue(next);
      return next;
    },
    [baseHandleFieldChange, canvas, pipeline]
  );

  // Canvas to form: in-place Textbox edits update formData without re-applying the binding.
  const handleFieldChangeFromCanvas = useCallback(
    (fieldKey: string, value: unknown) => {
      void baseHandleFieldChange(fieldKey, value as FormDataValue).then(snapshots.patchOthers);
    },
    [baseHandleFieldChange, snapshots]
  );

  // Wiring hooks.
  useCrossResolutionSync(canvas, snapshots.syncObjectAcrossResolutions);
  useCanvasToFormSync(
    canvas,
    canvasBinding.fieldBindings,
    formDataRef,
    pipeline.isApplyingRef,
    handleFieldChangeFromCanvas
  );
  useResolutionPreload(canvasBinding, formDataRef, activeResolution.id, computeDefaults, snapshots.get);

  const layerLabelResolver = useCallback<LayerLabelResolver>(
    (obj: FabricObject) => resolveLayerLabel(obj.pieceId, formData, canvasBinding.fieldBindings),
    [formData, canvasBinding.fieldBindings]
  );

  return (
    <EditorDataContext.Provider
      value={{
        formData,
        errors,
        fields,
        isFormComplete,
        handleFieldChange,
        setFieldError,
        validateForm,

        creativityType,
        canvasBinding,
        contentId,

        resolutions,
        activeResolution,
        setActiveResolutionId,

        editorRef,
        editorReady,
        contentReady,
        canvas,
        onEditorReady,
        onLoadingChange,
        layerLabelResolver,

        resolveResolutionJson: snapshots.resolveResolutionJson,

        saveResolutionSnapshot: snapshots.save,
        getResolutionSnapshot: snapshots.get,
        syncObjectAcrossResolutions: snapshots.syncObjectAcrossResolutions
      }}
    >
      {children}
    </EditorDataContext.Provider>
  );
};
